import React, { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { Mail, Plus, Send, Trash2 } from "lucide-react";
import {
  NotificationEmail,
  createNotificationEmail,
  deleteNotificationEmail,
  getNotificationEmails,
  sendTestNotificationEmail,
  setNotificationEmailEnabled,
} from "../services/notificationEmailApiService";

const errorText = (e: any, fallback: string) =>
  typeof e?.response?.data === "string" && e.response.data ? e.response.data : fallback;

const NotificationEmailsAdmin: React.FC = () => {
  const { instance, accounts } = useMsal();
  const [items, setItems] = useState<NotificationEmail[]>([]);
  const [smtpConfigured, setSmtpConfigured] = useState(true);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const [branch, setBranch] = useState("");
  const [warehouse, setWarehouse] = useState("");
  const [email, setEmail] = useState("");
  const [creating, setCreating] = useState(false);

  const refresh = async () => {
    if (accounts.length === 0) return;
    setLoading(true);
    try {
      const res = await getNotificationEmails(instance, accounts[0]);
      setItems(res.items);
      setSmtpConfigured(res.smtpConfigured);
    } catch {
      setMsg("Failed to load notification emails.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line
  }, [instance, accounts]);

  const handleAdd = async () => {
    if (!branch.trim() || !warehouse.trim() || !email.trim()) return;
    setCreating(true);
    setMsg(null);
    try {
      await createNotificationEmail(instance, accounts[0], {
        branch: branch.trim(),
        warehouse: warehouse.trim(),
        email: email.trim(),
      });
      setEmail("");
      await refresh();
    } catch (e: any) {
      setMsg(errorText(e, "Failed to add email."));
    } finally {
      setCreating(false);
    }
  };

  const handleToggle = async (item: NotificationEmail) => {
    setBusyId(item.id);
    setMsg(null);
    try {
      await setNotificationEmailEnabled(instance, accounts[0], item.id, !item.isEnabled);
      setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, isEnabled: !i.isEnabled } : i)));
    } catch {
      setMsg("Failed to update.");
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (item: NotificationEmail) => {
    if (!window.confirm(`Stop emailing ${item.email} for ${item.branch} ${item.warehouse}?`)) return;
    setMsg(null);
    try {
      await deleteNotificationEmail(instance, accounts[0], item.id);
      setItems((prev) => prev.filter((i) => i.id !== item.id));
    } catch {
      setMsg("Failed to delete.");
    }
  };

  const handleTest = async (item: NotificationEmail) => {
    setBusyId(item.id);
    setMsg(null);
    try {
      await sendTestNotificationEmail(instance, accounts[0], item.id);
      setMsg(`Test email sent to ${item.email}.`);
    } catch (e: any) {
      setMsg(errorText(e, "Test email failed."));
    } finally {
      setBusyId(null);
    }
  };

  const inputCls =
    "px-3 py-1.5 text-sm rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-background-dark text-slate-900 dark:text-white";

  return (
    <div className="bg-white dark:bg-surface-dark border border-slate-200 dark:border-slate-800 rounded-xl p-6 shadow-sm">
      <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-2">Batch Submission Emails</h2>
      <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed mb-4">
        When a plant sends a batch from its scans page, these addresses get an email with the cover sheet and a link
        to a batch status page (no login needed) that shows each ticket as accounting processes it. Branch and
        warehouse match the plant's scans page URL, e.g. <span className="font-mono">/scans/CLT/999</span>.
      </p>

      {!smtpConfigured && (
        <div className="mb-4 px-4 py-3 rounded-lg text-sm font-medium bg-amber-50 dark:bg-amber-500/10 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-500/20">
          SMTP isn't configured on the server yet, so no emails will be sent. Set the Smtp:* app settings on the API.
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <input value={branch} onChange={(e) => setBranch(e.target.value)} placeholder="Branch (CLT)" className={`${inputCls} w-28`} />
        <input value={warehouse} onChange={(e) => setWarehouse(e.target.value)} placeholder="Warehouse (999)" className={`${inputCls} w-36`} />
        <input
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleAdd()}
          placeholder="manager@concretesupplyco.com"
          type="email"
          className={`${inputCls} flex-1 min-w-[200px]`}
        />
        <button
          onClick={handleAdd}
          disabled={creating || !branch.trim() || !warehouse.trim() || !email.trim()}
          className="flex items-center gap-1.5 px-4 py-1.5 text-sm bg-emerald-600 hover:bg-emerald-500 text-white font-semibold rounded-md disabled:opacity-50 shrink-0"
        >
          <Plus size={14} />
          {creating ? "Adding..." : "Add"}
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Loading...</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-slate-400 italic">No notification emails yet.</div>
      ) : (
        <div className="space-y-2">
          {items.map((item) => (
            <div
              key={item.id}
              className={`flex items-center gap-3 p-2.5 rounded-lg border border-slate-200 dark:border-slate-800 ${
                item.isEnabled ? "" : "opacity-60"
              }`}
            >
              <Mail size={16} className="text-slate-400 shrink-0" />
              <span className="text-xs font-bold font-mono px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 shrink-0">
                {item.branch} {item.warehouse}
              </span>
              <span className="flex-1 min-w-0 text-sm text-slate-800 dark:text-slate-200 truncate">{item.email}</span>
              <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 select-none shrink-0">
                <input
                  type="checkbox"
                  checked={item.isEnabled}
                  disabled={busyId === item.id}
                  onChange={() => handleToggle(item)}
                  className="w-4 h-4"
                />
                Enabled
              </label>
              <button
                onClick={() => handleTest(item)}
                disabled={busyId === item.id || !smtpConfigured}
                className="flex items-center gap-1 px-2 py-1 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:text-primary disabled:opacity-40 shrink-0"
                title="Send a test email"
              >
                <Send size={13} /> Test
              </button>
              <button
                onClick={() => handleDelete(item)}
                className="p-1.5 text-slate-400 hover:text-rose-500 shrink-0"
                title="Delete"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      {msg && <div className="mt-3 text-sm text-slate-600 dark:text-slate-400">{msg}</div>}
    </div>
  );
};

export default NotificationEmailsAdmin;
